import { readStored, removeStored, writeStored } from "./safeStorage";
import { clearActiveTenantId } from "./activeTenant";
import { forgetUrlSecret } from "./urlSecrets";

/**
 * What belongs to the person signed in, in this browser's storage (ENG-3406).
 *
 * Two kinds, handled differently:
 *
 * - **Session keys**, cleared at every sign-out and every change of account. Log Out used to be the
 *   only path that cleared them, so a sign-out from the extension (`?logout=true`), an account
 *   deletion, or a sign-out in another tab left them for the next person: the previous person's
 *   active organization went out on every request as `X-Haven-Active-Tenant` (a 403, or silently the
 *   old organization if the next person also belonged to it), and a saved invitation pre-filled the
 *   old invitee's name and address on sign-up. App's auth listener now calls
 *   [clearPerUserStorage] whenever it sees an account end, which covers every path at once.
 * - **Remembered preferences** (Quick Start progress, the carousel dismissal, the Risk Report logo),
 *   kept across sign-outs on purpose, so they are stored under the account's Firebase uid instead
 *   ([userStorageKey]) and the next person on the browser starts from their own. The same pattern
 *   `RequestToJoinCard` already uses.
 */

/** The session keys: cleared, not kept, when the account changes. The active tenant is cleared too. */
/** `pendingOrgClaim` (ENG-3832): a demo org claim link, so the next person on the browser can't claim it. */
export const SESSION_STORED_KEYS = ["onboardingState", "onboardingCompleted", "pendingJoinToken", "pendingOrgClaim"] as const;

/** Clear everything this browser holds for the account that just ended. Safe where storage is blocked. */
export function clearPerUserStorage(): void {
  for (const key of SESSION_STORED_KEYS) removeStored(key);
  clearActiveTenantId();
  // ENG-3430 keeps the invitation token from the URL in memory too; the saved copy's twin goes with it,
  // or App mounting again (the root error boundary's retry) would pick the invitation back up.
  forgetUrlSecret("join");
  forgetUrlSecret("claim");
}

/**
 * [key] for one account: `key:uid`. Null with no uid, which [readUserStored] reads as nothing stored
 * and [writeUserStored] drops, so a value never lands where every account would read it.
 */
export function userStorageKey(key: string, uid: string | null | undefined): string | null {
  return uid ? `${key}:${uid}` : null;
}

export function readUserStored(key: string, uid: string | null | undefined): string | null {
  const scoped = userStorageKey(key, uid);
  return scoped ? readStored(scoped) : null;
}

export function writeUserStored(key: string, uid: string | null | undefined, value: string): void {
  const scoped = userStorageKey(key, uid);
  if (scoped) writeStored(scoped, value);
}

export function removeUserStored(key: string, uid: string | null | undefined): void {
  const scoped = userStorageKey(key, uid);
  if (scoped) removeStored(scoped);
}
